import { Component, ChangeDetectorRef, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Platform } from '@angular/cdk/platform';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseHorizontalNavigationComponent,
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseScrollbarDirective } from '@fuse/directives/scrollbar';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { MessagesComponent } from 'app/layout/common/messages/messages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SearchComponent } from 'app/layout/common/search/search.component';
import { ShortcutsComponent } from 'app/layout/common/shortcuts/shortcuts.component';
import { CommonModule, Location } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { PathEnum } from 'app/enums/pathEnum';
import { Establishment } from 'app/models/establishment';
import { Menu } from 'app/models/menu';
import { MenuCategory } from 'app/models/menu-category';
import { Subject, takeUntil } from 'rxjs';
import { EstablishmentService } from 'app/modules/establishment/establishment.service';
import { environment } from 'environments/environment';
import { ResourceUtils } from 'app/utils/resource.utils';
import { LanguagesUtils } from 'app/utils/languages.utils';
import { DragScrollDirective } from 'app/modules/shared/directives/drag-scroll.directive';
import {
    ACTIVE_SECTION_TOLERANCE_PX,
    CategoryNavBarComponent,
    CHIP_SCROLL_SETTLE_MS,
} from 'app/components/category-nav-bar/category-nav-bar.component';
import { QRCodeComponent } from 'angularx-qrcode';

@Component({
    selector: 'centered-layout',
    templateUrl: './centered.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        FuseVerticalNavigationComponent,
        FuseHorizontalNavigationComponent,
        FuseScrollbarDirective,
        MatButtonModule,
        MatIconModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        SearchComponent,
        ShortcutsComponent,
        MessagesComponent,
        NotificationsComponent,
        RouterOutlet,
        TranslocoModule,
        CommonModule,
        DragScrollDirective,
        CategoryNavBarComponent,
        QRCodeComponent,
    ],
})
export class CenteredLayoutComponent implements OnInit, OnDestroy {
    @ViewChild(CategoryNavBarComponent) categoryNavBar: CategoryNavBarComponent;

    navigation: Navigation;
    isScreenSmall: boolean;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    establishment: Establishment;

    // Loading state for the logo
    isLogoLoading = true;

    // Sticky nav properties
    showStickyNav = false;
    selectedCategoryUuid: string | null = null;
    isDesktop: boolean;
    private _stickyNavLocked = false;
    private _stickyNavUnlockTimer: ReturnType<typeof setTimeout> | undefined;
    private _boundScroll: () => void;
    private _phoneContainer: HTMLElement | null = null;

    constructor(
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _establishmentService: EstablishmentService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _platform: Platform,
        private location: Location
    ) {
        this.isDesktop = !this._platform.ANDROID && !this._platform.IOS;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for the single menu (if the establishment has exactly one menu)
     */
    get singleMenu(): Menu | null {
        return this.establishment?.menus?.length === 1 ? this.establishment.menus[0] : null;
    }

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    /**
     * Getter for website URL
     */
    get websiteUrl(): string {
        return environment.websiteUrl;
    }

    // /**
    //  * Get the URL for the text logo in light mode
    //  */
    // public get lightLogoTextUrl(): string {
    //     return this.establishmentLogoUrl || 'images/logo/logo-text.svg';
    // }

    // /**
    //  * Get the URL for the text logo in dark mode
    //  */
    // public get darkLogoTextUrl(): string {
    //     return this.establishmentLogoUrl || 'images/logo/logo-text-on-dark.svg';
    // }

    // /**
    //  * Get the URL for the navigation logo
    //  */
    // public get navigationLogoUrl(): string {
    //     return this.establishmentLogoUrl || 'images/logo/logo-text-on-dark.svg';
    // }

    /**
     * Get the URL for the small version logo
     */
    public get smallLogoUrl(): string {
        return this.establishmentLogoUrl || 'images/logo/logo.svg';
    }

    get qrCodeUrl(): string {
        const segments = this._router.url.split('/').filter(s => s);
        return segments.length > 0 ? window.location.origin + '/' + segments[0] : '';
    }

    get establishmentLogoUrl(): string {
        return this.establishment?.logo ? ResourceUtils.getResourceUrl(this.establishment.planCode, this.establishment.logo) : null;
    }

    // Method to handle logo load completion
    onLogoLoaded(): void {
        this.isLogoLoading = false;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this._navigationService.navigation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((navigation: Navigation) => {
                this.navigation = navigation;
            });

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });

        // Get establishment from URL
        const urlSegments = this._router.url.split('/');
        const establishmentUuid = urlSegments[urlSegments.length - 1];
        
        if (establishmentUuid) {
            this._establishmentService.establishment$
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(establishment => {
                    this.establishment = establishment;

                    // Set up sticky nav scroll listeners for single-menu establishments
                    if (this.singleMenu && !this._boundScroll) {
                        // Select first category by default
                        if (this.singleMenu.categories?.length > 0) {
                            this.selectedCategoryUuid = this.singleMenu.categories[0].uuid;
                        }

                        this._boundScroll = () => requestAnimationFrame(() => {
                            this.updateStickyNavVisibility();
                            this.updateVisibleCategoryWindow();
                        });

                        // Listen to window scroll (mobile) and phone container scroll (desktop)
                        window.addEventListener('scroll', this._boundScroll, { passive: true });
                        setTimeout(() => {
                            this._phoneContainer = document.querySelector('.phone-screen-size') as HTMLElement;
                            if (this._phoneContainer) {
                                this._phoneContainer.addEventListener('scroll', this._boundScroll, { passive: true });
                            }
                        });
                    }
                });
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();

        // Clean up scroll listeners
        if (this._boundScroll) {
            window.removeEventListener('scroll', this._boundScroll);
            this._phoneContainer?.removeEventListener('scroll', this._boundScroll);
        }
        clearTimeout(this._stickyNavUnlockTimer);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    isMenuPath(): boolean{
        return this._router.url.includes(PathEnum.MENU);
    }

    goBack(): void {
        // Extract establishment UUID from current URL
        const urlSegments = this._router.url.split('/');
        const establishmentUuid = urlSegments[urlSegments.length - 3]; // UUID is second to last segment in URL
        this._router.navigate([establishmentUuid]);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Sticky nav methods
    // -----------------------------------------------------------------------------------------------------

    getCategoryName(category: MenuCategory): string {
        return category.values.find(
            (value) => value.languageRefCode === LanguagesUtils.getMenuRemoteLang(this.establishment),
        )?.name || '';
    }

    getMenuTitle(): string {
        const lang = LanguagesUtils.getMenuRemoteLang(this.establishment);
        return this.singleMenu?.values.find(
            (v) => v.languageRefCode?.toUpperCase() === lang?.toUpperCase(),
        )?.name || '';
    }

    selectCategory(categoryUuid: string): void {
        this.selectedCategoryUuid = categoryUuid;

        // Prevent sticky nav from hiding during programmatic scroll
        this.lockStickyNav(800);

        this.scrollToCategory(categoryUuid);
        this.scrollToCategoryChipIfNeeded(categoryUuid);

        // Remove focus from the button
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement) {
            activeElement.blur();
        }
    }

    private get _stickyNavHeight(): number {
        const inner = document.querySelector('.sticky-nav-inner') as HTMLElement;
        return inner ? inner.offsetHeight : 100;
    }

    private scrollToCategory(categoryUuid: string): void {
        const element = document.getElementById(categoryUuid);
        if (!element) return;

        const navHeight = this._stickyNavHeight;

        if (this.isCardScrollMode()) {
            // Desktop: scroll within phone container
            const containerRect = this._phoneContainer.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            const offset = elementRect.top - containerRect.top + this._phoneContainer.scrollTop - navHeight;
            this._phoneContainer.scrollTo({ top: offset, behavior: 'smooth' });
        } else {
            // Mobile: window scroll
            const offset = element.getBoundingClientRect().top + window.pageYOffset - navHeight;
            window.scrollTo({ top: offset, behavior: 'smooth' });
        }
    }

    private scrollToCategoryChipIfNeeded(categoryUuid: string): void {
        if (this.categoryNavBar) {
            this.categoryNavBar.scrollChipIntoView(categoryUuid);
        }
    }

    private updateStickyNavVisibility(): void {
        const firstCategory = document.querySelector('.category') as HTMLElement;
        if (!firstCategory) return;

        const navHeight = this._stickyNavHeight;
        let threshold: number;
        if (this.isCardScrollMode()) {
            threshold = this._phoneContainer.getBoundingClientRect().top + navHeight;
        } else {
            threshold = navHeight;
        }

        const shouldShow = firstCategory.getBoundingClientRect().top <= threshold;
        if (this._stickyNavLocked && !shouldShow) return;
        if (shouldShow !== this.showStickyNav) {
            this.showStickyNav = shouldShow;
            this._changeDetectorRef.detectChanges();
        }
    }

    private updateVisibleCategoryWindow(): void {
        if (!this.singleMenu?.categories) return;
        if (this._stickyNavLocked) {
            // The scroll started by a chip tap is still moving: the tapped chip stays until it settles
            this.lockStickyNav(CHIP_SCROLL_SETTLE_MS);
            return;
        }

        const navHeight = this._stickyNavHeight;
        let threshold: number;
        if (this.isCardScrollMode()) {
            threshold = this._phoneContainer.getBoundingClientRect().top + navHeight;
        } else {
            threshold = navHeight;
        }

        // Last category whose top has reached the nav, give or take the sub-pixel gap where a
        // smooth scroll stops
        const categories = document.querySelectorAll('.category');
        for (let i = categories.length - 1; i >= 0; i--) {
            const category = categories[i] as HTMLElement;
            const rect = category.getBoundingClientRect();
            if (rect.top <= threshold + ACTIVE_SECTION_TOLERANCE_PX) {
                const categoryUuid = category.id;
                if (categoryUuid && categoryUuid !== this.selectedCategoryUuid) {
                    this.selectedCategoryUuid = categoryUuid;
                    this.scrollToCategoryChipIfNeeded(categoryUuid);
                    this._changeDetectorRef.detectChanges();
                }
                break;
            }
        }
    }

    /**
     * Holds the sticky nav and the scroll-based chip selection for `delay` ms. Every scroll event
     * meanwhile pushes the end back, so both only resume once the programmatic scroll has ended.
     */
    private lockStickyNav(delay: number): void {
        this._stickyNavLocked = true;
        clearTimeout(this._stickyNavUnlockTimer);
        this._stickyNavUnlockTimer = setTimeout(() => { this._stickyNavLocked = false; }, delay);
    }

    private isCardScrollMode(): boolean {
        if (!this._phoneContainer) return false;
        const style = getComputedStyle(this._phoneContainer);
        return style.overflowY === 'auto' || style.overflowY === 'scroll';
    }

}
